import dayjs from 'dayjs';
import { QrCodeVisitStatus } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useMemo, useState } from 'react';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useQrCodeVisits from 'v2/hooks/useQrCodeVisits/useQrCodeVisits';
import VisitsPageContent from 'v2/pages/Visits/components/VisitsPageContent';
import VisitsTable from 'v2/pages/Visits/components/VisitsTable';
import VisitStatTiles from 'v2/pages/Visits/components/VisitStatTiles';
import { computeVisitStats, filterVisitsByStat, VisitStatFilter } from 'v2/pages/Visits/utils/visitStats';
import { useVisitsSearch } from '../useVisitsSearch';

const Upcoming: React.FC = () => {
  const { translation } = useTranslation();
  const { showSnackbar } = useSnackbarContext();

  const [selectedStat, setSelectedStat] = useState(VisitStatFilter.TotalExpected);

  const handleFetchError = (message: string) => showSnackbar(message, { severity: 'error' });

  const { qrCodeVisits, loading } = useQrCodeVisits({
    handleFetchError,
    translation
  });

  const now = useMemo(() => dayjs(), []);

  const visits = useMemo(
    () =>
      qrCodeVisits
        .filter(
          (visit) => dayjs(visit.datetime).valueOf() >= now.valueOf() && visit.status !== QrCodeVisitStatus.Cancelled
        )
        .sort((a, b) => dayjs(a.datetime).valueOf() - dayjs(b.datetime).valueOf()),
    [qrCodeVisits, now]
  );

  const stats = useMemo(() => computeVisitStats(visits, now), [visits, now]);

  const visitsFilteredByStat = useMemo(
    () => filterVisitsByStat(visits, selectedStat, now),
    [visits, selectedStat, now]
  );

  const { filterStr, setFilterStr, filteredVisits, sections, chips, countCaption } = useVisitsSearch({
    visits: visitsFilteredByStat
  });

  const handleStatClick = (stat: VisitStatFilter) => {
    setSelectedStat((currentStat) => (currentStat === stat ? VisitStatFilter.TotalExpected : stat));
  };

  return (
    <VisitsPageContent
      breadcrumbTitle={translation.upcoming}
      statTiles={
        <VisitStatTiles stats={stats} isLoading={loading} selectedStat={selectedStat} onStatClick={handleStatClick} />
      }
      filterStr={filterStr}
      onFilterStrChange={setFilterStr}
      sections={sections}
      chips={chips}
      countCaption={countCaption}
      loading={loading}
      hasData={visits.length > 0}
      hasResults={filteredVisits.length > 0}
      // onCancelVisit intentionally omitted: cancel meeting UI not ready to ship yet
      table={<VisitsTable visits={filteredVisits} isLoading={loading} />}
      noDataTitle={translation.no_upcoming_visits}
      noDataContent={translation.no_upcoming_visits_scheduled}
    />
  );
};

export default Upcoming;
